/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Heading, HFlow, Status, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { useFlags } from 'config/useFlagsContext'
import { isEmpty } from 'lodash'
import { useMemo } from 'react'

import { ConsultaSeguimentoResultadoView } from '../consulta-seguimento/consulta-seguimento-resultado/ConsultaSeguimentoResultadoView'
import { ConsultaSeguimentoViewModel } from '../consulta-seguimento/model-consultaSeguimento'
import { MChatResultadoView } from '../mchat/mchat-resultado/MChatResultadoView'
import { MChatRiscoPrevioRelevanteInfoModel, MChatViewModel } from '../mchat/model-mchat'
import { MCHAT_RF_ACCORDION_SECTIONS, MChatRfAccordionSectionEnum } from '../model-mChatRf'

export interface MChatRfAccordionSectionProps {
  section: MChatRfAccordionSectionEnum
  value: MChatViewModel | ConsultaSeguimentoViewModel
  stepper: React.ReactNode
  primaryActions?: React.ReactNode
  secondaryActions?: React.ReactNode
  isConsultaSeguimentoAtiva?: boolean
  isRegistradoAgora?: boolean
  hideDivider?: boolean
  alertaMChatAnterior?: MChatRiscoPrevioRelevanteInfoModel
}

export function MChatRfAccordionSection(props: Readonly<MChatRfAccordionSectionProps>) {
  const {
    section,
    value,
    stepper,
    primaryActions,
    secondaryActions,
    isConsultaSeguimentoAtiva,
    isRegistradoAgora,
    hideDivider = false,
    alertaMChatAnterior,
  } = props

  const { M_CHAT_RF_ENABLED } = useFlags()
  const theme = useTheme()

  const classes = useMemo(() => createStyles(theme, section, isConsultaSeguimentoAtiva, hideDivider), [
    hideDivider,
    theme,
    section,
    isConsultaSeguimentoAtiva,
  ])

  const { title, description } = MCHAT_RF_ACCORDION_SECTIONS(M_CHAT_RF_ENABLED)[section]
  const hasValues = !isEmpty(value)

  return (
    <HFlow style={classes.section} justifyContent='flex-start' hSpacing={0.625}>
      {stepper}

      <VFlow vSpacing={0.5}>
        <HFlow justifyContent='space-between'>
          <HFlow alignItems='center' hSpacing={1}>
            <Heading level={2} style={classes.textColor}>
              {title}
            </Heading>
            {isRegistradoAgora && <Status type='info' text='Adicionado agora' />}
            {primaryActions}
          </HFlow>

          {secondaryActions && <HFlow hSpacing={0.5}>{secondaryActions}</HFlow>}
        </HFlow>
        {!hasValues && <Text style={classes.textColor}>{description}</Text>}

        {hasValues && section === MChatRfAccordionSectionEnum.MCHAT && (
          <MChatResultadoView
            value={value as MChatViewModel}
            alertaMChatAnterior={alertaMChatAnterior}
            isRegistradoAgora={isRegistradoAgora}
          />
        )}

        {hasValues && section === MChatRfAccordionSectionEnum.CONSULTA_SEGUIMENTO && (
          <ConsultaSeguimentoResultadoView value={value as ConsultaSeguimentoViewModel} />
        )}
      </VFlow>
    </HFlow>
  )
}

const createStyles = (
  theme: Theme,
  section: MChatRfAccordionSectionEnum,
  isConsultaSeguimentoAtiva: boolean,
  hideDivider: boolean
) => ({
  section: css`
    padding: 1rem;
    background-color: ${section === MChatRfAccordionSectionEnum.CONSULTA_SEGUIMENTO &&
    !isConsultaSeguimentoAtiva &&
    theme.pallete.gray.c90};
    border-top: ${hideDivider ? 'none' : `1px solid ${theme.pallete.gray.c80}`};
    grid-auto-columns: 1.5rem 1fr;
    position: relative;
  `,
  textColor: css`
    color: ${section === MChatRfAccordionSectionEnum.MCHAT || isConsultaSeguimentoAtiva
      ? theme.pallete.gray.c10
      : theme.pallete.gray.c40};
  `,
})
